import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import uuid from 'uuid/v4';

import { Text, Heading } from '@nerdwallet/react-typography';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import {
  textColorPicker,
  titleInput,
  boolCheckboxSpec,
  subtitleInput,
} from '../../lib/editing-configs';

import BackgroundColor from '../background-color/background-color';
import Row from '../row/row';
import VerticalSpacer from '../vertical-spacer/vertical-spacer';
import SmartButton from '../smart-button/smart-button';

import './last-chance.less';

const LastChance = ({
  title,
  subtitle,
  bgOptions,
  textColor,
  fontChronicle,

  // For SmartButton
  buttonURL,
  buttonText,
  ...smartButtonProps
}) => {
  // Only append trk params if this is not an #anchor link on the
  // current page.
  //
  // @todo in the future we can extract this to a new maybeSetTrk()
  //     util if there are multiple use cases.
  const isAnchorLink = (buttonURL || '').charAt(0) === '#';
  const modifiedUrl = isAnchorLink
    ? buttonURL
    : buttonURL &&
      setTrk({
        url: buttonURL,
        taxonomy: typeof window !== 'undefined' && window.nwPageTaxonomy,
        params: {
          [TrkTag.location]: 'LastChance',
          [TrkTag.element]: TrkElement.button,
          [TrkTag.copy]: buttonText,
        },
      });

  const nodeId = uuid();
  // useState is used to ensure that the trkId is unique per instance
  // and will only be set/updated when the component initially renders
  const [trkId] = useState(nodeId);

  const elementImpressionProps = {
    id: `last-chance-${trkId}`,
    elementName: 'LastChance',
  };

  return (
    <Impression eventType="element_impression" entityName="last_chance">
      <ElementImpression {...elementImpressionProps}>
        <BackgroundColor bgOptions={bgOptions}>
          {(backgroundColor) => (
            <div
              className="last-chance"
              style={{
                backgroundColor,
              }}
            >
              <Row centeredInParent className="last-chance__content">
                <VerticalSpacer desktop="p-60" tablet="p-60" mobile="p-30" />
                <Heading level="2" color={textColor} chronicle={fontChronicle}>
                  {title}
                </Heading>
                <VerticalSpacer desktop="p-24" tablet="p-24" mobile="p-24" />
                <Text color={textColor}>{subtitle}</Text>
                <VerticalSpacer desktop="p-24" tablet="p-24" mobile="p-30" />
                <Row centeredInParent>
                  <SmartButton
                    buttonURL={modifiedUrl}
                    buttonText={buttonText}
                    buttonClass="last-chance__button"
                    {...smartButtonProps}
                  />
                </Row>
                <VerticalSpacer desktop="p-60" tablet="p-60" mobile="p-24" />
              </Row>
            </div>
          )}
        </BackgroundColor>
      </ElementImpression>
    </Impression>
  );
};

LastChance.displayName = 'LastChance';

LastChance.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  ...BackgroundColor.propTypes,
  textColor: PropTypes.string,
  fontChronicle: PropTypes.bool,
  ...SmartButton.propTypes,
  secondaryCta: PropTypes.bool,
};

LastChance.defaultProps = {
  buttonType: 'custom',
  fontChronicle: false,
  secondaryCta: false,
  ...BackgroundColor.defaultProps,
};

LastChance.getEditingConfig = (componentDocument) => [
  ...BackgroundColor.getEditingConfig(componentDocument),
  textColorPicker,
  titleInput,
  boolCheckboxSpec('fontChronicle', 'Title in Chronicle?'),
  subtitleInput,
  ...SmartButton.getEditingConfig(componentDocument),
  boolCheckboxSpec('secondaryCta', 'Secondary CTA?'),
];

export default LastChance;
